import { AuthLoginPayload } from '@/types/auth';
import { Button, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

type LoginFormProps = {
  onSubmit: (data: AuthLoginPayload) => Promise<void>;
  onForgotPassword: () => void;
  defaultEmail?: string;
};

export function LoginForm({
  onSubmit,
  onForgotPassword,
  ...props
}: LoginFormProps) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useFormContext<AuthLoginPayload>();

  async function handleOnSubmit(data: AuthLoginPayload) {
    try {
      await onSubmit(data);
    } catch {
      reset(
        {},
        {
          keepValues: true,
          keepErrors: true,
        },
      );
      setError('root.serverError', {
        type: 'manual',
        message: 'E-mail or password is incorrect. Please try again.',
      });
    }
  }

  return (
    <form
      onSubmit={e => handleSubmit(handleOnSubmit)(e)}
      {...props}
      className="flex flex-col gap-6 text-mydra-black"
    >
      <div className="flex flex-col gap-6 mt-8 max-w-96 text-mydra-black">
        {errors.root?.serverError && (
          <Typography variant="subtitle2" className="text-red-500">
            {errors.root.serverError.message}
          </Typography>
        )}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors.email?.message}
              fullWidth
              placeholder="Email"
              aria-label="Email"
              type="email"
              hiddenLabel
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors.password?.message}
              fullWidth
              aria-label="Password"
              placeholder="Password"
              type="password"
            />
          )}
        />
        <div className="order-last">
          <Button
            variant="contained"
            size="small"
            className="self-start mt-8"
            type="submit"
            loading={isSubmitting}
          >
            Login
          </Button>
        </div>
        <Button
          variant="text"
          className="ml-auto"
          onClick={e => {
            e.preventDefault();
            onForgotPassword?.();
          }}
        >
          Forgot your password?
        </Button>
      </div>
    </form>
  );
}
